import { useEffect, useRef, useState } from 'react'
import { styled } from 'styled-components'

import { isProductionEnv } from '../../../../config/config'
import { getCsrfCookieToken } from '../../../../helpers/authHelper'
import { Handler } from '../../../../helpers/typeHelper'
import {
  CheckrCandidate,
  createBackgroundCheck,
  createCheckrCandidate,
  getCheckrTokenUrl,
  recordUserConsent,
} from '../../../../models/backgroundCheck'
import { User } from '../../../../models/user'
import { bgGray, midGray, royalBlue, white } from '../../../../theme/colors'
import { Embeds } from '../../../../vendor/checkr'
import { useUserProvider } from '../../../Context/UserContext'
import { Back } from '../../../Nav/Back'
import { InlineSpinner } from '../../../Spinner'

interface CheckrConsentProps {
  nextStep: Handler
  user: User
  previousStep: Handler
  setCdlInfoStep: Handler
}

const CONSENT_WRAP_ID = 'consent-wrap'

const checkrStyles = {
  useBaseline: true,
  body: {
    background: bgGray,
  },
  h1: {
    'padding-bottom': '15px',
  },
  '.disclosure-consent': {
    background: bgGray,
  },
  '.btn-primary': {
    background: royalBlue,
    color: white,
    border: 'none',
  },
  '.card': {
    'box-shadow': 'none',
  },
  '.card-block': {
    background: bgGray,
    border: 'none',
  },
  '.card-header': {
    background: bgGray,
    color: midGray,
    'padding-top': '15px',
    border: 'none',
  },
  '.scrollable': {
    border: 'none',
    padding: '0 20px 20px',
    margin: '0',
  },
  '.checkbox-container': {
    'font-style': 'italic',
    'font-weight': 'bold',
  },
  '#signature-field': {
    'margin-left': '10px',
  },
}

export function CheckrConsent({ user, nextStep, previousStep, setCdlInfoStep }: CheckrConsentProps) {
  const embedCreated = useRef(false)
  const [candidate, setCandidate] = useState<CheckrCandidate | null>(null)
  const { showBanner, clearUserCache } = useUserProvider()

  useEffect(() => {
    createCheckrCandidate(user.id)
      .then(setCandidate)
      .catch(async (err) => {
        await clearUserCache()
        setCdlInfoStep()
        showBanner('error', err)
      })
    // Just user id should be here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id])

  useEffect(() => {
    function createCheckrConsent() {
      if (!candidate) return
      if (embedCreated.current) return
      embedCreated.current = true

      async function handleConsentSuccess() {
        try {
          await recordUserConsent(user.id)
          await createBackgroundCheck(user.id)
          nextStep()
        } catch (err: any) {
          showBanner('error', err)
        }
      }

      if (user.driver.checkrHasActiveConsent) {
        return handleConsentSuccess()
      }

      const embed = new Embeds.DisclosureConsent({
        env: isProductionEnv() ? 'production' : 'staging',
        accountName: 'CloudTrucks',
        candidateFullName: `${user.firstName}${user.middleName ? ` ${user.middleName} ` : ' '}${
          user.lastName
        }`,
        candidateId: candidate.checkrCandidateId,
        language: 'en',
        packageSlug: candidate.consentPackageName,
        sessionTokenPath: getCheckrTokenUrl(user.id),
        sessionTokenRequestHeaders: () => ({
          'Content-Type': 'application/json',
          'x-csrftoken': getCsrfCookieToken(),
        }),
        workLocationState: candidate.complianceLocationState,
        driverLicenseState: user.driver.licenseState,
        onLoadDisclosureError(...args: any) {
          console.error(args)
          showBanner('error', { message: 'Loading disclosure failed. Please try again or contact us.' })
        },
        onConsentSubmitError(...args: any) {
          console.error(args)
          showBanner('error', { message: 'Submitting the consent failed. Please try again or contact us.' })
        },
        onConsentSubmitSuccess: handleConsentSuccess,
        styles: checkrStyles,
      })
      embed.render(`#${CONSENT_WRAP_ID}`)
    }

    createCheckrConsent()
  })

  return (
    <>
      {!candidate && <Back onClick={previousStep} />}
      <InlineSpinner className="spinner" />
      <Wrap id={CONSENT_WRAP_ID}></Wrap>
      <style>
        {`
        .spinner:has( + #${CONSENT_WRAP_ID} iframe) {
          display: none;
        }
      `}
      </style>
    </>
  )
}

const Wrap = styled.div`
  max-width: 1024px;
  margin: 0 auto;
`
